import React, { useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Button, Stack,
    CircularProgress, Divider, Alert,
} from "@mui/material";
import WorkIcon from "@mui/icons-material/Work";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PreviewIcon from "@mui/icons-material/Preview";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ScheduleIcon from "@mui/icons-material/Schedule";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import { useBrand } from "../../../../contexts/BrandContext";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

const fmtDate = (v) => v ? new Date(v).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "–";

/** Renders a selector AST as readable conditions */
function SelectorReadonlyView({ selector }) {
    if (!selector || Object.keys(selector).length === 0) return null;

    const operatorLabel = (op) => {
        const labels = { eq: "=", neq: "≠", gt: ">", gte: "≥", lt: "<", lte: "≤", between: "entre", in: "dans", contains: "contient", exists: "existe" };
        return labels[op] || op;
    };

    const renderLeaf = (leaf, idx) => {
        if (!leaf || !leaf.field) return null;
        const valStr = Array.isArray(leaf.value) ? leaf.value.join(", ") : String(leaf.value ?? "");
        return (
            <Box key={idx} display="flex" alignItems="center" gap={1} py={0.5} flexWrap="wrap">
                <Chip label={leaf.field} size="small" sx={{
                    fontFamily: "monospace", fontSize: 11, fontWeight: 700,
                    bgcolor: leaf.field.startsWith("customer.") ? "#e0f2fe" : "#ede9fe",
                    color: leaf.field.startsWith("customer.") ? "#0369a1" : "#7c3aed",
                }} />
                <Chip label={operatorLabel(leaf.operator)} size="small"
                    sx={{ fontSize: 10, fontWeight: 700, bgcolor: "#f1f5f9", color: "#475569" }} />
                <Chip label={valStr || "–"} size="small"
                    sx={{ fontSize: 11, fontFamily: "monospace", bgcolor: "#fefce8", color: "#854d0e" }} />
            </Box>
        );
    };

    const combinator = ["and", "or", "not"].find((k) => k in selector);
    const items = combinator && Array.isArray(selector[combinator]) ? selector[combinator] : null;

    return (
        <Box mt={1} p={1.5} sx={{ borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
            <Box display="flex" alignItems="center" gap={0.75} mb={1}>
                <FilterAltIcon sx={{ fontSize: 14, color: SECTION_COLORS.internalJobs.main }} />
                <Typography fontSize={11} fontWeight={700} color={SECTION_COLORS.internalJobs.main}>
                    Filtre de sélection
                    {combinator && (
                        <Chip label={combinator.toUpperCase()} size="small" sx={{
                            ml: 1, fontSize: 10, fontWeight: 800,
                            bgcolor: `${SECTION_COLORS.internalJobs.main}15`,
                            color: SECTION_COLORS.internalJobs.main,
                        }} />
                    )}
                </Typography>
            </Box>
            {items
                ? items.map((leaf, idx) => renderLeaf(leaf, idx))
                : renderLeaf(selector, 0)
            }
        </Box>
    );
}

const JsonBlock = ({ label, value }) => {
    if (!value || (typeof value === "object" && Object.keys(value).length === 0)) return null;
    return (
        <Box mt={1} p={1.5} sx={{ borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
            <Typography fontSize={11} fontWeight={700} color={SECTION_COLORS.internalJobs.main} mb={0.5}>{label}</Typography>
            <Typography component="pre" fontSize={11}
                sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all", color: "#1a1a2e", m: 0 }}>
                {JSON.stringify(value, null, 2)}
            </Typography>
        </Box>
    );
};

const ViewInternalJobDialog = ({ open, job, onClose, onEditRequest, onRunSuccess }) => {
    const { currentBrand } = useBrand();
    const brandName = currentBrand?.name || job?.brand || "";

    const [running, setRunning] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [runResult, setRunResult] = useState(null);
    const [previewResult, setPreviewResult] = useState(null);
    const [actionError, setActionError] = useState(null);

    if (!job) return null;

    const handleRun = async () => {
        setRunning(true);
        setRunResult(null);
        setActionError(null);
        try {
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/internal-jobs/${job.id}/run`, {
                method: "POST",
                headers: { "X-Brand": brandName },
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.detail || `Erreur HTTP ${res.status}`);
            setRunResult(data);
            if (onRunSuccess) onRunSuccess();
        } catch (err) { setActionError(err.message); } finally { setRunning(false); }
    };

    const handlePreview = async () => {
        setPreviewing(true);
        setPreviewResult(null);
        setActionError(null);
        try {
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/internal-jobs/${job.id}/preview`, {
                method: "POST",
                headers: { "X-Brand": brandName },
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.detail || `Erreur HTTP ${res.status}`);
            setPreviewResult(data);
        } catch (err) { setActionError(err.message); } finally { setPreviewing(false); }
    };

    const handleClose = () => {
        setRunResult(null);
        setPreviewResult(null);
        setActionError(null);
        onClose();
    };

    const infoRows = [
        { label: "ID", value: job.id },
        { label: "Clé", value: job.job_key },
        { label: "Nom", value: job.name || "–" },
        { label: "Description", value: job.description || "–" },
        { label: "Marque", value: job.brand || "Globale" },
        { label: "Type de transaction", value: job.transaction_type },
        { label: "Segment ciblé", value: job.segment_id ? String(job.segment_id) : "Aucun (tous les clients)" },
        { label: "Prochain run", value: fmtDate(job.next_run_at) },
        { label: "Dernier run", value: fmtDate(job.last_run_at) },
        { label: "Dernier statut", value: job.last_status || "–" },
        { label: "Créé le", value: fmtDate(job.created_at) },
        { label: "Modifié le", value: fmtDate(job.updated_at) },
    ];
    if (job.last_error) infoRows.push({ label: "Dernière erreur", value: job.last_error });

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.internalJobs.gradient }}>
                        <WorkIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            {job.name || job.job_key}
                        </Typography>
                        {job.description && (
                            <Typography fontSize={11} color="text.secondary" sx={{ maxWidth: 380, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {job.description}
                            </Typography>
                        )}
                        {!job.description && (
                            <Typography fontSize={11} color="text.secondary">Détail du job interne</Typography>
                        )}
                    </Box>
                </Box>
                <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                {/* Status banner */}
                <Box display="flex" alignItems="center" gap={1} mb={2} px={2} py={1.25}
                    sx={{
                        borderRadius: 2,
                        bgcolor: job.active ? "#f0fdf4" : "#fef2f2",
                        border: `1px solid ${job.active ? "#86efac" : "#fca5a5"}`
                    }}>
                    {job.active
                        ? <CheckCircleIcon sx={{ fontSize: 18, color: "#16a34a" }} />
                        : <CancelIcon sx={{ fontSize: 18, color: "#DC2626" }} />}
                    <Typography fontSize={13} fontWeight={600} color={job.active ? "#16a34a" : "#DC2626"}>
                        {job.active ? "Actif" : "Inactif"}
                    </Typography>
                    {job.last_status && (
                        <Chip label={job.last_status} size="small" sx={{
                            ml: "auto", fontWeight: 700, fontSize: 10,
                            bgcolor: job.last_status === "SUCCESS" ? "#dcfce7" : "#fee2e2",
                            color: job.last_status === "SUCCESS" ? "#15803d" : "#dc2626",
                        }} />
                    )}
                </Box>

                {/* Schedule info */}
                {job.schedule?.cron && (
                    <Box display="flex" alignItems="center" gap={1} mb={2} px={2} py={1}
                        sx={{ borderRadius: 2, bgcolor: "#f0f9ff", border: "1px solid #bae6fd" }}>
                        <ScheduleIcon sx={{ fontSize: 18, color: "#0369a1" }} />
                        <Typography fontSize={13} fontFamily="monospace" color="#0369a1" fontWeight={600}>
                            {job.schedule.cron}
                        </Typography>
                        {job.schedule.timezone && (
                            <Chip label={job.schedule.timezone} size="small"
                                sx={{ fontSize: 10, bgcolor: "#e0f2fe", color: "#0369a1" }} />
                        )}
                    </Box>
                )}

                {/* Info rows */}
                <Stack spacing={1} mb={2}>
                    {infoRows.map(({ label, value }) => (
                        <Box key={label} display="flex" alignItems="flex-start" justifyContent="space-between"
                            px={2} py={1} sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Typography fontSize={12} color="text.secondary" fontWeight={500} sx={{ minWidth: 150 }}>{label}</Typography>
                            <Typography fontSize={12} fontWeight={600} color="#1a1a2e" sx={{ textAlign: "right", wordBreak: "break-all" }}>
                                {value}
                            </Typography>
                        </Box>
                    ))}
                </Stack>

                {/* Selector readable view */}
                {job.selector && Object.keys(job.selector).length > 0 && (
                    <SelectorReadonlyView selector={job.selector} />
                )}
                {job.payload_template && Object.keys(job.payload_template).length > 0 && (
                    <JsonBlock label="payload_template" value={job.payload_template} />
                )}

                <Divider sx={{ my: 2 }}>
                    <Typography fontSize={11} color="text.secondary">Actions manuelles</Typography>
                </Divider>

                {/* Preview result */}
                {previewResult && (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                        <Typography fontWeight={700} fontSize={13}>Aperçu</Typography>
                        <Typography fontSize={12}>
                            {previewResult.count} client(s) ciblé(s) par ce job pour la date du {previewResult.date}.
                        </Typography>
                        {previewResult.sample?.length > 0 && (
                            <Typography fontSize={11} mt={0.5} color="text.secondary">
                                Exemple : {previewResult.sample.slice(0, 3).map(c => c.profileId).join(", ")}
                                {previewResult.count > 3 ? ` +${previewResult.count - 3} autres` : ""}
                            </Typography>
                        )}
                    </Alert>
                )}

                {/* Run result */}
                {runResult && (
                    <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                        <Typography fontWeight={700} fontSize={13}>Exécution terminée</Typography>
                        <Typography fontSize={12}>
                            {runResult.created} transaction(s) créée(s) · {runResult.targetCustomers} client(s) traité(s)
                            {runResult.idempotentExisting > 0 && ` · ${runResult.idempotentExisting} doublon(s) ignoré(s)`}
                            {runResult.failed > 0 && ` · ${runResult.failed} échec(s)`}
                        </Typography>
                    </Alert>
                )}

                {actionError && (
                    <Alert severity="error" sx={{ mb: 1, borderRadius: 2 }}>{actionError}</Alert>
                )}

                <Box display="flex" gap={1} flexWrap="wrap">
                    <Button variant="outlined" size="small"
                        startIcon={previewing ? <CircularProgress size={14} /> : <PreviewIcon />}
                        onClick={handlePreview} disabled={!job.active || previewing || running}
                        sx={{ textTransform: "none", borderRadius: 2, borderColor: "#0369a1", color: "#0369a1", "&:hover": { bgcolor: "#e0f2fe" } }}>
                        {previewing ? "Chargement..." : "Aperçu clients ciblés"}
                    </Button>
                    <Button variant="contained" size="small"
                        startIcon={running ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                        onClick={handleRun} disabled={!job.active || running || previewing}
                        sx={{ textTransform: "none", borderRadius: 2, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }}>
                        {running ? "Exécution en cours..." : "Exécuter maintenant"}
                    </Button>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={handleClose} sx={{ color: "text.secondary", textTransform: "none" }}>Fermer</Button>
                <Button variant="contained" onClick={() => { handleClose(); onEditRequest(job); }}
                    startIcon={<EditIcon />}
                    sx={{ background: SECTION_COLORS.internalJobs.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    Modifier
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ViewInternalJobDialog;
